import React, { memo } from 'react';
import { Modal, Select } from 'antd';
import { useCardTransitionModal } from './useCardTransitionModal';
import { CardTransitionModalProps, ProductOption } from './types';
import * as S from './styles';
import DSButton from '../../../../../components/DSButton';

const { Option } = Select;

interface StepOneProps {
  availableProducts: ProductOption[];
  selectedProductId: number | string | null;
  onSelectProduct: (value: number | string) => void;
  isLoading: boolean;
}

const SelectProductStep: React.FC<StepOneProps> = ({
  availableProducts,
  selectedProductId,
  onSelectProduct,
  isLoading,
}) => (
  <S.StepContainer>
    <S.InstructionText>
      Selecione o novo produto para realizar a transição deste cartão:
    </S.InstructionText>
    <Select 
      placeholder="Selecione um produto elegível" 
      onChange={onSelectProduct}
      value={selectedProductId}
      style={{ width: "100%" }}
      disabled={isLoading}
      data-testid="select_produto_vipagem"
    >
      {availableProducts.map((productItem) => (
        <Option key={productItem.product_id} value={productItem.product_id}>
          {productItem.product_name}
        </Option>
      ))}
    </Select>
  </S.StepContainer>
);

interface StepTwoProps {
  currentProductDescription: string;
  newProductName: string;
}

const EMPTY_PRODUCTS_MESSAGE =
  "Não existem produtos disponíveis para transição para este emissor.";

const EmptyProductsStep: React.FC = () => (
  <S.StepContainer>
    <S.EmptyProductsMessage>{EMPTY_PRODUCTS_MESSAGE}</S.EmptyProductsMessage>
  </S.StepContainer>
);

const ConfirmTransitionStep: React.FC<StepTwoProps> = ({
  currentProductDescription,
  newProductName,
}) => (
  <S.StepContainer>
    <S.InstructionText>
      Tem certeza que deseja solicitar a transição deste cartão?
    </S.InstructionText>

    <S.SummaryBox>
      <S.SummaryText>Produto Atual: <strong>{currentProductDescription}</strong></S.SummaryText>
      <S.SummaryText>Novo Produto: <strong>{newProductName}</strong></S.SummaryText>
    </S.SummaryBox>

    <S.DisclaimerText>
      * A operação ficará como pendente até a validação e efetivação na processadora.
    </S.DisclaimerText>
  </S.StepContainer>
);


const CardTransitionModal: React.FC<CardTransitionModalProps> = ({
  isOpen,
  onClose,
  clientId,
  cardNumber,
  product,
  availableBrandProducts = [],
  onSuccess,
}) => {
  const {
    currentStep,
    selectedProductId,
    setSelectedProductId,
    isLoading,
    handleNextStep,
    handlePrevStep,
    handleConfirm,
    handleClose,
  } = useCardTransitionModal({ clientId, cardNumber, isOpen, onSuccess, onClose });

  const selectedProductDetails = availableBrandProducts.find(
    (p) => p.product_id === selectedProductId
  );
  const newProductName = selectedProductDetails?.product_name || "";
  const currentProductDescription = product?.desc_prod || "Não identificado";
  const hasAvailableProducts = availableBrandProducts.length > 0;

  const modalTitle = currentStep === 1 
    ? "Solicitar Transição para Bandeira" 
    : "Confirmar Solicitação para Bandeira";

  return (
    <Modal
      title={modalTitle}
      open={isOpen} 
      onCancel={handleClose}
      maskClosable={!isLoading}
      closable={!isLoading}
      footer={null}
      destroyOnClose
      width={600}
    >
      {currentStep === 1 && (
        hasAvailableProducts ? (
          <SelectProductStep
            availableProducts={availableBrandProducts}
            selectedProductId={selectedProductId}
            onSelectProduct={setSelectedProductId}
            isLoading={isLoading}
          />
        ) : (
          <EmptyProductsStep />
        )
      )}

      {currentStep === 2 && (
        <ConfirmTransitionStep
          currentProductDescription={currentProductDescription}
          newProductName={newProductName}
        />
      )}

      <S.FooterActions>
        {currentStep === 1 ? (
          <>
            <DSButton
              type="button"
              buttonText="Cancelar"
              buttonType="secondary"
              isCancelType 
              onClick={handleClose}
              height={30}
              style={{ padding: "0.4rem 1.5rem" }} 
              dataTestId="botao_cancelar_vipagem"
              disabled={isLoading}
              isDisabled={isLoading}
            />
            {hasAvailableProducts && (
              <DSButton
                type="button"
                buttonText="Solicitar"
                buttonType="secondary" 
                onClick={handleNextStep}
                height={30}
                style={{ padding: "0.4rem 1.5rem" }} 
                dataTestId="botao_avancar_vipagem"
                disabled={isLoading || !selectedProductId}
                isDisabled={isLoading || !selectedProductId}
              />
            )}
          </>
        ) : (
          <>
            <DSButton
              type="button"
              buttonText="Voltar"
              buttonType="secondary"
              isCancelType
              onClick={handlePrevStep}
              height={30}
              style={{ padding: "0.4rem 1.5rem" }} 
              dataTestId="botao_voltar_vipagem"
              disabled={isLoading}
              isDisabled={isLoading}
            />
            <DSButton
              type="button"
              buttonText="Confirmar Solicitação"
              buttonType="secondary"
              onClick={handleConfirm}
              height={30}
              style={{ padding: "0.4rem 1.5rem" }} 
              dataTestId="botao_confirmar_vipagem"
              isLoading={isLoading} 
              disabled={isLoading}
              isDisabled={isLoading}
            />
          </>
        )}
      </S.FooterActions>
    </Modal>
  );
};

export default memo(CardTransitionModal);